import React, { useMemo, useState } from "react";
import { SectionTitle, InputCss } from "../../_parts.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import CreditHistoryField from "../shared/CreditHistoryField.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { useBtlUploads } from "../shared/useBtlUploads.js";
import { buildUploadLabelMap, isValidEmail } from "../shared/formValidation.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import { arePhonesValid, isValidPhone, PHONE_ERROR_MOBILE } from "../../../../utils/validatePhone.js";
import { IF_FORM_WIZARD_STEPS, IF_UPLOAD_BUCKETS } from "../shared/borrowerUploadConfig.js";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const MIN_PARTNERS = 2;
const MAX_PARTNERS = 4;

export default function IfPartnershipForm({
  onBack,
  onNext,
  initialPayload,
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("if-partnership", productId || initialPayload?.productId));
  const initial = savedDraft?.if?.partnership || initialPayload?.if?.partnership || null;

  const [partners, setPartners] = useState(
    initial?.partners?.length
      ? initial.partners
      : [
          { id: uid(), firstName: "", surname: "", phone: "", email: "", credit: "" },
          { id: uid(), firstName: "", surname: "", phone: "", email: "", credit: "" },
        ]
  );
  const { uploads, uploadingFiles, handleUpload, handleRemove } = useBtlUploads(initial?.uploads || {});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const lead = partners[0] || {};
  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));
  const update = (id, key, val) => setPartners((arr) => arr.map((p) => (p.id === id ? { ...p, [key]: val } : p)));

  const payloadObj = useMemo(
    () => ({
      ...(initialPayload || {}),
      if: {
        ...(initialPayload?.if || {}),
        borrowerType: "Partnership",
        partnership: { partners, uploads },
      },
      formData: {
        ...(initialPayload?.formData || {}),
        credit: lead.credit,
        credit_history: lead.credit,
      },
    }),
    [initialPayload, partners, uploads, lead.credit]
  );

  const validateDetails = () => {
    const err = {};
    if (partners.length < MIN_PARTNERS) err._partners = `At least ${MIN_PARTNERS} partners are required.`;
    if (!lead.firstName?.trim()) err.lead_firstName = "First Name is required.";
    if (!lead.surname?.trim()) err.lead_surname = "Surname is required.";
    if (!isValidPhone(lead.phone)) err.lead_phone = PHONE_ERROR_MOBILE;
    if (!lead.email?.trim()) err.lead_email = "Email is required.";
    else if (!isValidEmail(lead.email)) err.lead_email = "Enter a valid email address.";
    if (!lead.credit) err.lead_credit = "Please select credit history.";
    setFieldErrors(err);
    setTouched({
      lead_firstName: true,
      lead_surname: true,
      lead_phone: true,
      lead_email: true,
      lead_credit: true,
      _partners: true,
    });
    return !Object.keys(err).length;
  };

  const stepErrors = useMemo(
    () => ({
      details: Object.keys(fieldErrors).length > 0,
      documents: false,
      review: false,
    }),
    [fieldErrors]
  );

  const validateStep = async (stepIndex, stepId) => {
    if (stepId === "details" || stepIndex === 0) return validateDetails();
    return true;
  };

  const finish = (extras = {}) => {
    onNext?.({
      type: "partnership",
      partners,
      credit: lead.credit,
      uploads,
      payloadObj,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(IF_UPLOAD_BUCKETS), []);

  return (
    <>
      <BorrowerSteppedForm
        title="INVOICE FINANCE — PARTNERSHIP"
        subtitle="Please complete all required fields and upload necessary documents"
        steps={IF_FORM_WIZARD_STEPS}
        draftKey={getDraftStorageKey("if-partnership", productId || initialPayload?.productId)}
        draftSnapshot={payloadObj}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        isSubmitting={isSubmitting}
        detailsContinueDisabled={!arePhonesValid(partners.map((p, i) => ({ value: p.phone, required: i === 0 })))}
        renderDetails={() => (
          <div className="space-y-4">
            <SectionTitle>Personal Information</SectionTitle>
            {fieldErrors._partners && touched._partners ? (
              <p className="text-xs text-rose-600" role="alert">{fieldErrors._partners}</p>
            ) : null}
            {partners.map((p, idx) => (
              <div key={p.id} className="rounded-xl border border-slate-200 p-3 md:p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-900">{idx === 0 ? "Main Partner" : `Partner ${idx + 1}`}</p>
                  {partners.length > MIN_PARTNERS ? (
                    <button type="button" className="text-xs font-medium text-red-500" onClick={() => setPartners((list) => list.filter((x) => x.id !== p.id))}>Remove</button>
                  ) : null}
                </div>
                <FormFieldRow>
                  <FormField id={`if-p-${p.id}-fn`} label="First Name" required={idx === 0} error={idx === 0 ? fieldErrors.lead_firstName : undefined} touched={idx === 0 ? touched.lead_firstName : false}>
                    <input className="input w-full" value={p.firstName} onChange={(e) => update(p.id, "firstName", e.target.value)} onBlur={() => idx === 0 && touch("lead_firstName")} />
                  </FormField>
                  <FormField id={`if-p-${p.id}-sn`} label="Surname" required={idx === 0} error={idx === 0 ? fieldErrors.lead_surname : undefined} touched={idx === 0 ? touched.lead_surname : false}>
                    <input className="input w-full" value={p.surname} onChange={(e) => update(p.id, "surname", e.target.value)} onBlur={() => idx === 0 && touch("lead_surname")} />
                  </FormField>
                  <FormField id={`if-p-${p.id}-ph`} label="Phone Number" required={idx === 0} error={idx === 0 ? fieldErrors.lead_phone : undefined} touched={idx === 0 ? touched.lead_phone : false}>
                    <PhoneField
                      value={p.phone}
                      required={idx === 0}
                      onChange={(next) => update(p.id, "phone", next)}
                      onBlur={() => idx === 0 && touch("lead_phone")}
                    />
                  </FormField>
                  <FormField id={`if-p-${p.id}-em`} label="Email" required={idx === 0} error={idx === 0 ? fieldErrors.lead_email : undefined} touched={idx === 0 ? touched.lead_email : false}>
                    <input type="email" className="input w-full" value={p.email} onChange={(e) => update(p.id, "email", e.target.value)} onBlur={() => idx === 0 && touch("lead_email")} />
                  </FormField>
                </FormFieldRow>
                {idx === 0 ? (
                  <div className="mt-4">
                    <CreditHistoryField id="if-partner-credit" value={lead.credit} error={fieldErrors.lead_credit} touched={touched.lead_credit} onChange={(opt) => update(lead.id, "credit", opt)} />
                  </div>
                ) : null}
              </div>
            ))}
            {partners.length < MAX_PARTNERS ? (
              <button type="button" className="text-sm font-semibold text-sky-600" onClick={() => setPartners((p) => [...p, { id: uid(), firstName: "", surname: "", phone: "", email: "", credit: "" }])}>
                + Add partner
              </button>
            ) : null}
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel buckets={IF_UPLOAD_BUCKETS} uploads={uploads} uploadingFiles={uploadingFiles} onUpload={handleUpload} onRemove={handleRemove} />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            documentsStepIndex={1}
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                rows: [{ label: "Credit History", value: lead.credit }],
                content: partners.map((p, i) => (
                  <p key={p.id} className="text-sm">
                    <span className="text-slate-500">{i === 0 ? "Main partner" : `Partner ${i + 1}`}: </span>
                    {p.firstName} {p.surname} · {p.phone} · {p.email}
                  </p>
                )),
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
